/**
 * EventReconciler - Deduplicação de eventos para Realtime Perceptivo
 * 
 * Evita que evento de INTENÇÃO (local) duplique com evento do BANCO.
 * O banco sempre vence em caso de conflito real.
 * 
 * REGRAS:
 * - Evento de intenção marca ID temporário
 * - Evento do banco verifica se já foi processado
 * - TTL curto (60s) para limpar memória
 * - Best-effort: se falhar, banco vence
 */

interface ProcessedEvent {
  timestamp: number;
  type: 'intent' | 'db';
  entityType: string;
}

// ============= CONFIGURAÇÃO =============

const EVENT_TTL_MS = 60_000; // 60 segundos
const CLEANUP_INTERVAL_MS = 30_000; // Limpar a cada 30 segundos

// ============= IMPLEMENTAÇÃO =============

class EventReconcilerImpl {
  private processedEvents = new Map<string, ProcessedEvent>();
  private cleanupTimer: ReturnType<typeof setInterval> | null = null;

  constructor() {
    // Iniciar limpeza automática
    if (typeof window !== 'undefined') {
      this.startCleanup();
    }
  }

  /**
   * Gera ID único para um evento
   * Formato: {entityType}_{entityId}_{action}
   */
  generateEventId(entityType: string, entityId: number | string, action?: string): string {
    const base = `${entityType}_${entityId}`;
    return action ? `${base}_${action}` : base;
  }

  /**
   * Marca evento de INTENÇÃO como processado
   * Retorna o eventId gerado
   */
  markIntentProcessed(entityType: string, tempId: number): string {
    const eventId = this.generateEventId(entityType, tempId, 'intent');
    
    this.processedEvents.set(eventId, {
      timestamp: Date.now(),
      type: 'intent',
      entityType,
    });

    return eventId;
  }

  /**
   * Marca evento do BANCO como processado
   * Retorna o eventId gerado
   */
  markDbProcessed(entityType: string, realId: number): string {
    const eventId = this.generateEventId(entityType, realId, 'db');
    
    this.processedEvents.set(eventId, {
      timestamp: Date.now(),
      type: 'db',
      entityType,
    });

    return eventId;
  }

  /**
   * Verifica se evento do banco é duplicata de intenção recente
   * Se tempId estiver mapeado para realId, considera duplicata
   */
  isDbEventDuplicate(entityType: string, realId: number, tempId?: number): boolean {
    // Se temos tempId, verificar se a intenção já foi processada
    if (tempId !== undefined) {
      const intentEventId = this.generateEventId(entityType, tempId, 'intent');
      if (this.processedEvents.has(intentEventId)) {
        // Intenção existe = banco está confirmando = duplicata para UI
        return true;
      }
    }

    // Verificar se já processamos este ID real do banco
    const dbEventId = this.generateEventId(entityType, realId, 'db');
    if (this.processedEvents.has(dbEventId)) {
      return true;
    }

    return false;
  }

  /**
   * Limpa eventos antigos (TTL expirado)
   */
  cleanup(): void {
    const now = Date.now();
    let cleanedCount = 0;

    for (const [eventId, event] of this.processedEvents) {
      if (now - event.timestamp > EVENT_TTL_MS) {
        this.processedEvents.delete(eventId);
        cleanedCount++;
      }
    }

    if (cleanedCount > 0 && import.meta.env.DEV) {
      console.log(`[EventReconciler] Limpou ${cleanedCount} eventos expirados`);
    }
  }

  /**
   * Inicia limpeza automática
   */
  private startCleanup(): void {
    if (this.cleanupTimer) return;

    this.cleanupTimer = setInterval(() => {
      this.cleanup();
    }, CLEANUP_INTERVAL_MS);
  }

  /**
   * Para limpeza automática (para testes)
   */
  stopCleanup(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = null;
    }
  }

  /**
   * Limpa todos os eventos (para testes)
   */
  clear(): void {
    this.processedEvents.clear();
  }

  /**
   * Retorna tamanho do cache (para debug)
   */
  size(): number {
    return this.processedEvents.size;
  }
}

// Singleton global
export const eventReconciler = new EventReconcilerImpl();

// ============= DEBUG =============

if (typeof window !== 'undefined' && import.meta.env.DEV) {
  (window as unknown as Record<string, unknown>).__eventReconciler = eventReconciler;
}
